import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Checkbox } from '@viewthespace/components';
import { TextInput } from '@components/shared/forms';
import RichTextEditorInput from '@components/shared/forms/RichTextEditorInput';
import { useFormikContext } from 'formik';
import { AdminMicrosite } from '@root/types';
import { ContentBlockWrapper, ContentBlockProps } from '..';
import ColorPicker from '../../ColorPicker';
import TextColorSelector from '../../TextColorSelector';
import { useMicrositeFormContext } from '../../utils';

export default function Certifications({ id, index, anchorSlug }: ContentBlockProps) {
  const { colorGroups, microsite, resetKey, changeFontColorWithColorChange, sendPreviewTo } =
    useMicrositeFormContext();
  const { values, setFieldValue, setFieldTouched } = useFormikContext<Partial<AdminMicrosite>>();

  const flags = useFlags();
  const { t } = useTranslation('admin');

  const isCertificationsSectionHidden = !values.certificationsSectionIsVisible;

  return (
    <ContentBlockWrapper
      contentBlockId={id}
      title={
        flags['market-office.flexible-layouts-on-microsite']
          ? t('microsite.sections.certifications')
          : t('microsite.sections.featuresCertifications')
      }
      section="featuresCertifications"
      onClick={() => sendPreviewTo({ page: 'features', element: anchorSlug })}
      index={index}
      isHidden={isCertificationsSectionHidden}
    >
      <Checkbox
        label={t('microsite.hideSection')}
        className="[&_input]:!w-[16px] [&_input]:!h-[16px]"
        backgroundStyle="filled"
        isChecked={isCertificationsSectionHidden}
        onChange={e => {
          setFieldValue('certificationsSectionIsVisible', !e.value);
          setFieldTouched('certificationsSectionIsVisible', true);
        }}
      />
      <ColorPicker
        colorGroups={colorGroups}
        name="certificationsBackgroundColor"
        labelText={t('microsite.backgroundColor')}
        onChange={changeFontColorWithColorChange('certificationsTextColor')}
      />
      {flags['market-office.text-color-selector'] && (
        <TextColorSelector name="certificationsTextColor" />
      )}
      <ColorPicker
        colorGroups={colorGroups}
        name="certificationsBorderColor"
        labelText={t('microsite.borderColor')}
        hasTransparentOption
      />

      {flags['market.building-descriptions-support-rich-text'] ? (
        <RichTextEditorInput
          name="certificationsTitle"
          labelText={t('microsite.sectionTitle')}
          hideLabel
          editorClassName="min-h-[100px] bg-white"
          excludeMenuItems={['bulletList']}
          initialContent={microsite.certificationsTitle}
          defaultTextSize="header32"
          resetKey={resetKey}
          fast
          shouldDebounce
        />
      ) : (
        <TextInput name="certificationsTitle" labelText={t('microsite.sectionTitle')} />
      )}
    </ContentBlockWrapper>
  );
}
